// src/components/annotation/ImageAnnotationComponentV2.tsx

import React, {
  useCallback,
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react'
import { useGetContentImageQuery } from '../../services/contentApi'
import { useGetTagsQuery } from '../../services/tagApi'
import { useAppSelector } from '../../store/hooks'
import { selectAllBoundingBoxes, selectedBBoxClassIndex } from '../../store/slices/annotationSlice'
import Canvas from './Canvas'
import { calculateZoomForBBox } from './utils/drawing'
import { useImageAnnotation } from './hooks/useImageAnnotation'
import TagSelector from '../tools/TagSelector'
import { FaSun } from 'react-icons/fa'
import { selectSelectedProjectId } from '../../store/slices/projectSlice'
import { AnnotationDataBoundingBox } from '../../types/models/annotation'
import { useGetProjectQuery } from '../../services/projectsApi'

export interface ImageAnnotationComponentV2Handle {
  undo: () => void
  redo: () => void
  delete: () => void
  recenterImage: () => void
  setZoom: (zoom: number) => void
  isResizing: () => boolean
  selectBBox: (box: AnnotationDataBoundingBox | null) => void
  removeAllTags: (tagId: string) => void
  zoomToBBox: (box: AnnotationDataBoundingBox) => void
}

interface ImageAnnotationComponentV2Props {
  contentId: string
  useBBoxes: boolean
  focusedBBoxIndex?: number | undefined
  // setFocusedBBoxIndex: (index: number | undefined) => void
  zoomLevel?: number
  showBBoxes?: boolean
  onZoomChange?: (zoom: number) => void
  filteredBoxes?: AnnotationDataBoundingBox[]
  hoveredBoxIndex?: number
  showConfidence: boolean
  labelMode?: 'class' | 'class+confidence'
  selectOnFocus?: boolean
  isEditable?: boolean
}

const ImageAnnotationComponentV2 = forwardRef<
  ImageAnnotationComponentV2Handle,
  ImageAnnotationComponentV2Props
>(
  (
    {
      contentId,
      useBBoxes,
      focusedBBoxIndex,
      // setFocusedBBoxIndex,
      zoomLevel: externalZoomLevel,
      showBBoxes: externalShowBBoxes,
      filteredBoxes,
      hoveredBoxIndex,
      showConfidence,
      labelMode = 'class+confidence',
      selectOnFocus = true,
      isEditable = true,
      onZoomChange,
    },
    ref
  ) => {
    const selectedProjectId = useAppSelector(selectSelectedProjectId)
    const { data: project } = useGetProjectQuery(selectedProjectId || '', {
      skip: !selectedProjectId,
    })
    const allBoundingBoxes = useAppSelector(selectAllBoundingBoxes)
    const selectedBBoxClassIdx = useAppSelector(selectedBBoxClassIndex)

    // Add cache-busting state for image refreshing
    const [imageCacheTimestamp, setImageCacheTimestamp] = useState<number>(Date.now())

    // Listen for annotation updates to refresh images
    useEffect(() => {
      const handleAnnotationUpdate = () => {
        setImageCacheTimestamp(Date.now())
      }

      window.addEventListener('annotation-updated', handleAnnotationUpdate)
      window.addEventListener('reset-image-loading', handleAnnotationUpdate)

      return () => {
        window.removeEventListener('annotation-updated', handleAnnotationUpdate)
        window.removeEventListener('reset-image-loading', handleAnnotationUpdate)
      }
    }, [])

    const { data: imageUrl, isLoading: isImageLoading } = useGetContentImageQuery(
      {
        ContentID: contentId,
        project_id: selectedProjectId || '',
        dataset_id: project?.datasetid || '',
        _t: imageCacheTimestamp,
      },
      {
        skip: !contentId || !selectedProjectId || !project?.datasetid,
      }
    )

    const { data: tags } = useGetTagsQuery(
      {
        project_id: selectedProjectId || '',
        dataset_id: project?.datasetid || '',
      },
      { skip: !selectedProjectId || !project?.datasetid }
    )

    // const [showHeatmap, setShowHeatmap] = useState(false)
    // const [showConfidence, setShowConfidence] = useState(false)

    const {
      canvasRef,
      imageRef,
      handleMouseEvent,
      handleWheel: internalHandleWheel,
      zoomLevel,
      panOffset,
      newBoundingBox,
      setNewBoundingBox,
      handleTagSelect,
      mousePosition,
      recenterImage,
      undo,
      redo,
      contrast,
      setContrast,
      selectedBBox,
      handleBoxSelection,
      handleDoubleClick,
      popupState,
      setPopupState,
      handleBoxDelete,
      setZoomLevel: setZoomLevelHook,
      setPanOffset,
      handleCloseTagSelector,
      resizingHandle,
      removeAllTags,
      creationAxisStart,
      creationPhase,
    } = useImageAnnotation(useBBoxes, isEditable, filteredBoxes)

    // Remove the internal zoom state since we're using the hook's zoom
    const [internalShowBBoxes, setInternalShowBBoxes] = useState(true)

    const isSyncingFromExternal = useRef(false)

    // Use the hook's zoom level as the source of truth
    useEffect(() => {
      if (externalZoomLevel !== undefined) {
        isSyncingFromExternal.current = true
        setZoomLevelHook(externalZoomLevel)
        if (externalZoomLevel === 0.85) {
          setPanOffset({ x: 0, y: 0 })
        }
      }
    }, [externalZoomLevel, setZoomLevelHook, setPanOffset])

    useEffect(() => {
      if (externalShowBBoxes !== undefined) {
        setInternalShowBBoxes(externalShowBBoxes)
      }
    }, [externalShowBBoxes])

    // When internal zoom changes (e.g., wheel or keyboard), notify parent
    useEffect(() => {
      if (typeof onZoomChange === 'function') {
        if (isSyncingFromExternal.current) {
          // Skip echoing changes initiated by external prop to avoid loops
          isSyncingFromExternal.current = false
        } else if (externalZoomLevel === undefined || zoomLevel !== externalZoomLevel) {
          onZoomChange(zoomLevel)
        }
      }
    }, [zoomLevel, externalZoomLevel, onZoomChange])

    const handleContrastSlider = useCallback(
      (newContrast: number) => {
        setContrast(newContrast)
      },
      [setContrast]
    )

    // useEffect(() => {
    //   if (focusedBBoxIndex !== undefined && canvasRef.current) {
    //     const sourceList =
    //       filteredBoxes && filteredBoxes.length > 0 ? filteredBoxes : allBoundingBoxes
    //     // Ensure we resolve the correct box when sourceList is a filtered view
    //     const referenceBox = allBoundingBoxes[focusedBBoxIndex]
    //     const targetBBox = referenceBox
    //       ? sourceList.find((b) => b.class_index === referenceBox.class_index) || referenceBox
    //       : sourceList[focusedBBoxIndex]
    //     if (!targetBBox) {
    //       !!setFocusedBBoxIndex && setFocusedBBoxIndex(undefined)
    //       return
    //     }
    //     // Only zoom to the bounding box, don't select it for editing
    //     if (
    //       project?.annotation_type === 'bounding_box' ||
    //       project?.annotation_type === 'oriented_bounding_box'
    //     ) {
    //       const bBoxScaleX =
    //         (canvasRef.current?.width || 1) / (imageRef?.current?.naturalWidth || 1)
    //       const bBoxScaleY =
    //         (canvasRef.current?.height || 1) / (imageRef?.current?.naturalHeight || 1)
    //       const { zoomLevel: newZoom, panOffset: newPan } = calculateZoomForBBox(
    //         targetBBox,
    //         canvasRef.current,
    //         bBoxScaleX,
    //         bBoxScaleY,
    //         imageRef?.current?.naturalWidth,
    //         imageRef?.current?.naturalHeight
    //       )
    //       setZoomLevelHook(newZoom)
    //       setPanOffset(newPan)
    //       if (selectOnFocus) {
    //         setSelectedBBox(targetBBox)
    //       }
    //     }
    //   }
    //   !!setFocusedBBoxIndex && setFocusedBBoxIndex(undefined)
    // }, [
    //   canvasRef,
    //   focusedBBoxIndex,
    //   allBoundingBoxes,
    //   setPanOffset,
    //   setZoomLevelHook,
    //   setFocusedBBoxIndex,
    //   project?.annotation_type,
    //   imageRef,
    //   filteredBoxes,
    //   selectOnFocus,
    //   setSelectedBBox,
    // ])

    useEffect(() => {
      // recenterImage()
      setPopupState({
        show: false,
        position: { x: 0, y: 0 },
        box: null,
      })
      setNewBoundingBox(null)
      // dispatch(clearUndoStack())
      // dispatch(clearRedoStack())
      // selection cleared upstream
      // setFocusedBBoxIndex(undefined)
    }, [focusedBBoxIndex, setNewBoundingBox, setPopupState])

    useEffect(() => {
      if (!selectedBBox) {
        setPopupState({
          show: false,
          position: { x: 0, y: 0 },
          box: null,
        })
      }
    }, [selectedBBox, setPopupState])

    // Reset zoom and pan when switching to a different image
    useEffect(() => {
      recenterImage()
    }, [contentId, recenterImage])

    // Expose functions via ref
    useImperativeHandle(
      ref,
      () => ({
        undo,
        redo,
        delete: () => {
          // Find the box by class_index from Redux state
          const boxToDelete =
            selectedBBoxClassIdx !== undefined
              ? allBoundingBoxes.find((box) => box.class_index === selectedBBoxClassIdx)
              : selectedBBox
          handleBoxDelete(boxToDelete || null)
        },
        recenterImage,
        setZoom: setZoomLevelHook,
        isResizing: () => resizingHandle !== null,
        selectBBox: (box: AnnotationDataBoundingBox | null) => {
          handleBoxSelection(box)
        },
        zoomToBBox: (box: AnnotationDataBoundingBox) => {
          if (!box) return
          if (!canvasRef.current || !imageRef.current) return
          const bBoxScaleX = canvasRef.current.width / (imageRef.current.naturalWidth || 1)
          const bBoxScaleY = canvasRef.current.height / (imageRef.current.naturalHeight || 1)
          const { zoomLevel: newZoom, panOffset: newPan } = calculateZoomForBBox(
            box,
            canvasRef.current,
            bBoxScaleX,
            bBoxScaleY,
            imageRef.current.naturalWidth,
            imageRef.current.naturalHeight
          )
          setZoomLevelHook(newZoom)
          setPanOffset(newPan)
          handleBoxSelection(box)
        },
        removeAllTags: (tagId: string) => removeAllTags(tagId),
      }),
      [
        undo,
        redo,
        handleBoxDelete,
        selectedBBox,
        selectedBBoxClassIdx,
        allBoundingBoxes,
        recenterImage,
        setZoomLevelHook,
        setPanOffset,
        resizingHandle,
        handleBoxSelection,
        removeAllTags,
        canvasRef,
        imageRef,
      ]
    )

    // Let the hook handle wheel events directly
    const handleWheel = useCallback(
      (e: React.WheelEvent<HTMLCanvasElement>) => {
        // e.preventDefault() // Prevent page scroll
        internalHandleWheel(e)
        // Only notify parent of zoom changes from UI controls, not from wheel events
      },
      [internalHandleWheel]
    )

    useEffect(() => {
      const canvas = canvasRef.current
      if (!canvas) return

      // Add wheel event listener with passive: false
      const handleWheelEvent = (e: WheelEvent) => {
        e.preventDefault()
        internalHandleWheel(e as unknown as React.WheelEvent<HTMLCanvasElement>)
      }

      canvas.addEventListener('wheel', handleWheelEvent, { passive: false })

      return () => {
        canvas.removeEventListener('wheel', handleWheelEvent)
      }
    }, [canvasRef, internalHandleWheel])

    return (
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        {isImageLoading ? (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }}
          >
            <div className="spinner-border text-light" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <Canvas
            canvasRef={canvasRef}
            imageRef={imageRef}
            imageUrl={imageUrl}
            handleMouseEvent={handleMouseEvent}
            handleWheel={handleWheel}
            zoomLevel={zoomLevel}
            panOffset={panOffset}
            newBoundingBox={newBoundingBox}
            selectedBBox={selectedBBox ?? null}
            onBoxSelect={handleBoxSelection}
            tags={tags}
            useBBoxes={useBBoxes}
            showBBoxes={
              project?.annotation_type === 'bounding_box' ||
              project?.annotation_type === 'oriented_bounding_box'
                ? internalShowBBoxes
                : false
            }
            mousePosition={mousePosition}
            contrast={contrast}
            // showHeatmap={showHeatmap}
            handleDoubleClick={handleDoubleClick}
            showConfidence={showConfidence}
            labelMode={labelMode}
            canvasWidth={0}
            canvasHeight={0}
            bBoxIndex={focusedBBoxIndex}
            filteredBoxes={filteredBoxes}
            hoveredBoxIndex={hoveredBoxIndex}
            creationAxisStart={creationAxisStart}
            creationPhase={creationPhase}
            isEditable={isEditable}
          />
        )}
        <div className="controls-top">
          <div className="contrast-control">
            <FaSun
              className="contrast-icon"
              onClick={() => handleContrastSlider(0)}
              style={{ cursor: 'pointer' }}
              title="Reset contrast"
            />
            <input
              type="range"
              min="-100"
              max="100"
              value={contrast}
              onChange={(e) => handleContrastSlider(Number(e.target.value))}
              className="form-range"
              title="Adjust image contrast"
            />
          </div>
        </div>
        {popupState.show && (
          <TagSelector
            onTagSelect={handleTagSelect}
            position={popupState.position}
            onClose={handleCloseTagSelector}
          />
        )}
      </div>
    )
  }
)

ImageAnnotationComponentV2.displayName = 'ImageAnnotationComponentV2'

export default ImageAnnotationComponentV2
